import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { useAuth } from '@/context/AuthContext'
import { checkStatus } from '@/services/mobi-api/handsetDti'
import { resolveImageUrl } from '@/utils/constants'
import useIntl from '@/utils/intl'
import { useLanguage } from '@/context/LanguageContext'
import { useToast } from '@/context/ToastContext'
import { useTheme } from '@/context/ThemeContext'
import { Close, Done } from '@mui/icons-material'
import {
  Box,
  Divider,
  MobileStepper,
  Step,
  StepLabel,
  Stepper,
  Typography,
  StepContent
} from '@mui/material'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import EMongolia from './eMongolia'
import EmailInput from './verification/emailInput'
import NumberInput from './verification/numberInput'
import Verification from './verification'
import PaymentInfo from './paymentInfo'
import Loader from '@/components/ui/loader'
import LeasingInfo from './leasingInfo/index'
import TermsPage from './leasingInfo/terms'
import ConfirmZMS from './paymentInfo/confirmZMS'
import StatusInfo from './leasingInfo/status'
import PendingInfo from './leasingInfo/pending'

export interface Obj {
  instanceId?: string
  email?: string
  phoneNumber?: string
  choosenPackage?: string
  rateplan?: string
  danUrl?: string
  status?: string
  leasingRes?: LeasingRes
  errorMessage?: string
}

interface HandsetResponse {
  id?: string
  error?: string
  cause?: { messageEn?: string, messageMn?: string }
  message?: { messageEn?: string, messageMn?: string }
  danRedirectUrl?: string
  status?: string
  leasingRes?: LeasingRes
  errorMessage?: string
}
interface LeasingRes {
  totalAmount?: number
  usedAmount?: number
  expirationDate?: Date
  info?: number
}

type ErrorType = 'BLACKLIST' | 'UNDERAGE' | 'CORP_ERROR'
interface Props {
  onClose: () => void
  onContinue: () => void
}

const LeasingServices: FunctionComponent<Props> = ({ onClose, onContinue }) => {
  const { translate } = useIntl()
  const { locale } = useLanguage()
  const { showError } = useToast()
  const { theme } = useTheme()
  const { isWebView } = useAuth()
  const searchParams = useSearchParams()
  const [currentStep, setCurrentStep] = useState(0)
  const [subStep, setSubStep] = useState<'step1' | 'number' | 'email'>('step1')
  const [objs, setObj] = useState<Obj>({})
  const [isIphoneClient, setIsIphoneClient] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)
  const [showLeasingInfo, setShowLeasingInfo] = useState<boolean>(false)
  const [showTerms, setShowTerms] = useState<boolean>(false)
  const langue = useMemo(() => searchParams.get('lang'), [searchParams])
  const token = useMemo(() => searchParams.get('t'), [searchParams])
  const hasFetched = useRef(false)
  const [errorType, setErrorType] = useState<ErrorType | undefined>()

  useEffect(() => {
    const userAgent = navigator.userAgent ?? (window as any).opera
    setIsIphoneClient(userAgent.includes('iPhone'))
  }, [])

  useEffect(() => {
    const rawStep = searchParams.get('step')
    const openParam = searchParams.get('open')
    const id = searchParams.get('_id')
    const externalSuccess = searchParams.get('externalSuccess')
    let stepNum: number | null = null
    let newSubStep: 'step1' | 'number' | 'email' = 'step1'
    let newShowLeasingInfo = false
    let newObj: Obj = { ...objs }

    if (rawStep && !isNaN(parseInt(rawStep))) {
      stepNum = parseInt(rawStep)
    } else if (openParam) {
      const openParams = new URLSearchParams(openParam.split('?')[1] || '')
      const stepFromOpen = openParams.get('step')
      if (stepFromOpen && !isNaN(parseInt(stepFromOpen))) {
        stepNum = parseInt(stepFromOpen)
      } else {
        const match = openParam.match(/step(\d)/)
        if ((match != null) && !isNaN(parseInt(match[1]))) {
          stepNum = parseInt(match[1])
        }
      }
    }
    if (isWebView && token) {
      try {
        const decodedIsdn = Buffer.from(token, 'base64').toString('ascii')
        newObj = {
          ...newObj,
          phoneNumber: decodedIsdn,
          choosenPackage: 'option1'
        }
        newShowLeasingInfo = false
        stepNum = 1 
        newSubStep = 'step1' 
      } catch (error) {
        console.error('Failed to decode token:', error)
        showError(translate('error'))
      }
    } else if (stepNum !== null && externalSuccess === 'true') {
      stepNum = stepNum
      newSubStep = 'step1'
    } else if (openParam === 'leasing') {
      stepNum = 0
      newSubStep = 'step1'
      newShowLeasingInfo = false
    } else if (stepNum !== null) {
      stepNum = stepNum
      newSubStep = 'step1'
    } else if (id) {
      stepNum = 1
      newSubStep = 'step1'
    }

    setCurrentStep(stepNum ?? 0)
    setSubStep(newSubStep)
    setShowLeasingInfo(newShowLeasingInfo)
    setObj(newObj)
  }, [])

  useEffect(() => {
    if (hasFetched.current || !searchParams) return
    hasFetched.current = true
    const fetchData = async () => {
      setLoading(true)
      try {
        if (langue != null && token != null) {
          const temp = {
            isdn: Buffer.from(token, 'base64').toString('ascii'),
            channel: isWebView ? 'APP' : 'WEB',
            lang: langue ?? locale,
            theme
          }
          const res: HandsetResponse | undefined = await checkStatus(temp)
          if (res == null) {
            showError(translate('request.failed'))
            return
          }
          if (res.errorMessage === 'BLACKLIST') {
            setErrorType('BLACKLIST')
            return
          } else if (res.errorMessage === 'UNDERAGE') {
            setErrorType('UNDERAGE')
            return
          } else if (res.errorMessage === 'CORP_ERROR') {
            setErrorType('CORP_ERROR')
            return
          }

          setObj((prev) => ({
            ...prev,
            instanceId: res.id,
            danUrl: res.danRedirectUrl,
            status: res.status,
            leasingRes: res.leasingRes
          }))
          if (!res.id && !res.status && !res.danRedirectUrl && !res.errorMessage && (res.leasingRes == null)) {
            showError(translate('request.failed'))
          }
        } else if (!isWebView) {
          const res = await checkStatus({
            isdn: 'empty',
            channel: 'WEB',
            theme
          })
          if (res?.id) {
            setObj((prev) => ({ ...prev, instanceId: res.id }))
          } else {
            showError(translate('request.failed'))
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error)
        showError(translate('request.failed'))
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    if (objs.status === 'PENDING' || objs.status === 'COMPLETE') {
      if (objs.danUrl) {
        setCurrentStep(-1)
      }
    }
  }, [objs.status, objs.danUrl])

  const steps = [
    {
      label: translate('leasing.step1Title'),
      description: translate('leasing.step1Desc')
    },
    {
      label: translate('leasing.step2Title'),
      description: translate('leasing.step2Desc')
    },
    {
      label: translate('leasing.step3Title'),
      description: translate('leasing.step3Desc')
    },
    {
      label: translate('leasing.step4Title'),
      description: translate('leasing.step4Desc')
    }
  ]

  const handleStep1Next = (obj: Obj): void => {
    setObj({ ...objs, ...obj })
    setSubStep(obj.choosenPackage === 'option1' ? 'number' : 'email')
  }

  const handleInputNext = (obj: Obj): void => {
    setObj({ ...objs, ...obj })
    setSubStep('step1')
    setCurrentStep(currentStep + 1)
  }

  const updateObj = (obj: Obj): void => {
    setObj((prev) => ({ ...prev, ...obj }))
  }

  const handleBack = (): void => {
    console.log('handleBack called', {
      subStep,
      currentStep,
      isWebView,
      showLeasingInfo,
      showTerms
    })
    if (showTerms) {
      setShowTerms(false)
    } else if (currentStep === 1 && isWebView) {
      setShowLeasingInfo(true)
      setCurrentStep(0)
      setSubStep('number')
    } else if (subStep === 'number' && isWebView) {
      setShowLeasingInfo(true)
    } else if (subStep === 'step1' && currentStep > 0) {
      setCurrentStep(currentStep - 1)
    } else if (subStep !== 'step1') {
      setSubStep('step1')
    } else {
      console.log('No back action taken, exiting webview or closing')
      if (isWebView) {
        exitWebview()
      } else {
        onClose()
      }
    }
  }

  const exitWebview = (): void => {
    console.log('exitWebview called')
    const webView =
      (window as any).mobiBridge || (window as any).ReactNativeWebview
    if (webView) {
      const data = { key: 'exit' }
      webView.postMessage(JSON.stringify(data))
    } else {
      console.warn('Webview bridge not found')
      onClose()
    }
  }

  const handleLeasingInfoNext = (): void => {
    console.log('handleLeasingInfoNext called')
    setShowLeasingInfo(false)
    setSubStep('step1')
    setCurrentStep(1)
  }

  const handleTermsClick = (): void => {
    console.log('Showing TermsPage')
    setShowTerms(true)
  }

  const handleTermsBack = (): void => {
    console.log('Returning to LeasingInfo from TermsPage')
    setShowTerms(false)
  }

  const handleTermsContinue = (): void => {
    console.log('handleTermsContinue called, jumping to EMongolia')
    setShowTerms(false)
    setShowLeasingInfo(false)
    setSubStep('step1')
    setCurrentStep(1)
  }

  const stepIcon = (index: number): JSX.Element => {
    return (
      <>
        {index === currentStep && (
          <div className='bg-secondary p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <span className='text-textInverse font-extrabold text-xs md:text-sm'>
              {index + 1}
            </span>
          </div>
        )}
        {index < currentStep && (
          <div className='bg-[#F0FDF4] border border-[#16A34A] p-2 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <Done className='text-[#16A34A] text-xs md:text-sm font-bold' />
          </div>
        )}
        {index > currentStep && (
          <div className='bg-[#231F1F1A] p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <span className='text-[#B0B0B0] font-extrabold text-xs md:text-sm'>
              {index + 1}
            </span>
          </div>
        )}
      </>
    )
  }

  return (
    <div className='flex md:flex-row flex-col h-full md:h-[90vh]'>
      {loading
        ? (
          <div className='flex items-center justify-center w-full h-[50vh]'>
            <Loader />
          </div>
          )
        : (
          <>
            <div className='bg-surfaceInverse flex flex-col xl:w-1/4 lg:w-2/5'>
              <div className='lg:p-14 md:p-8 p-4'>
                <div className='md:flex flex-col items-start hidden'>
                  <Image
                    draggable={false}
                    width={50}
                    height={50}
                    src={resolveImageUrl('/uploads/mobicom-logo-hq.png')}
                    alt='Mobicom logo'
                    className='w-16'
                  />
                  <div className='mt-2'>
                    <h1 className='lg:text-2xl md:text-xl font-bold text-textHigh'>
                      {translate('quick.leasing')}
                    </h1>
                    <span className='text-textMid text-xs 2xl:text-sm font-medium'>
                      {translate('simple')}
                    </span>
                  </div>
                </div>
                <div className='md:hidden flex justify-between items-center'>
                  <div className='flex items-center gap-2'>
                    {!(
                      objs.status === 'PENDING' ||
                    objs.status === 'COMPLETE' ||
                    (showLeasingInfo && !showTerms)
                    ) && (
                      <>
                        <h1 className='font-bold text-sm text-textHigh'>
                          {translate('quick.leasing')}
                        </h1>
                        <MobileStepper
                          className='bg-surfaceInverse'
                          color='error'
                          variant='dots'
                          sx={{
                            '.MuiMobileStepper-dotActive': {
                              backgroundColor: 'red'
                            }
                          }}
                          steps={steps.length}
                          position='static'
                          activeStep={currentStep}
                          backButton={<></>}
                          nextButton={<></>}
                        />
                      </>
                    )}
                    {(showTerms || currentStep >= 1) && (
                      <div className='flex items-center'>
                        <DarkButton
                          type='soft'
                          fullwidth
                          size='sm'
                          onClick={handleBack}
                        >
                          {translate('back')}
                        </DarkButton>
                      </div>
                    )}
                  </div>
                  {(currentStep >= 1) && (
                    <DarkIconButton
                      type='soft'
                      size='sm'
                      icon={<Close />}
                      onClick={() => {
                        if (!isWebView && isIphoneClient) {
                          onClose()
                        } else {
                          console.log('Close button clicked')
                          isWebView ? exitWebview() : onClose()
                        }
                      }}
                    />
                  )}
                </div>
              </div>
              <Divider className='hidden md:flex' />
              <div className='lg:p-14 md:p-8'>
                <div className='hidden md:flex'>
                  <Stepper orientation='vertical' activeStep={currentStep}>
                    {steps.map((step, index) => (
                      <Step key={index}>
                        <StepLabel StepIconComponent={() => stepIcon(index)}>
                          <Typography className='text-sm font-bold text-textHigh'>
                            {step.label}
                          </Typography>
                        </StepLabel>
                        <StepContent>
                          <Typography className='text-xs text-textMid font-medium'>
                            {step.description}
                          </Typography>
                          {index > 0
                            ? (
                              <Box sx={{ mt: 2 }}>
                                <DarkButton
                                  type='soft'
                                  size='sm'
                                  onClick={() => {
                                    setCurrentStep(index - 1)
                                    setSubStep('step1')
                                  }}
                                >
                                  {translate('back')}
                                </DarkButton>
                              </Box>
                              )
                            : null}
                        </StepContent>
                      </Step>
                    ))}
                  </Stepper>
                </div>
              </div>
            </div>
            <Divider orientation='vertical' flexItem />
            <div className='bg-surfaceWebGrey lg:p-6 md:p-3 w-full h-full'>
              <div className='flex justify-end w-full'>
                <div className='hidden md:flex'>
                  <DarkIconButton
                    type='soft'
                    size='sm'
                    icon={<Close />}
                    onClick={() => {
                      console.log('Close button clicked')
                      isWebView ? exitWebview() : onClose()
                    }}
                  />
                </div>
              </div>
              <div className='h-full flex justify-center items-center'>
                {isWebView
                  ? (
                      showTerms
                        ? (
                          <TermsPage onBack={handleTermsBack} onContinue={handleTermsContinue} />
                          )
                        : 
                        objs.status === 'COMPLETE'
                          ? (
                            <StatusInfo onTermsClick={handleTermsClick} obj={objs} />
                            )
                          : 
                          objs.status === 'PENDING'
                            ? (
                              <PendingInfo status={objs.status} />
                              )
                            : objs.errorMessage === 'BLACKLIST'
                              ? (
                                <EMongolia
                                  instanceId={objs.instanceId ?? ''}
                                  errorType='BLACKLIST'
                                />
                                )
                              : objs.errorMessage === 'UNDERAGE'
                                ? (
                                  <EMongolia
                                    instanceId={objs.instanceId ?? ''}
                                    errorType='UNDERAGE'
                                  />
                                  )
                                : objs.errorMessage === 'CORP_ERROR'
                                  ? (
                                    <EMongolia
                                      instanceId={objs.instanceId ?? ''}
                                      errorType='CORP_ERROR'
                                    />
                                    )
                                  : 
                                  showLeasingInfo ? 
                                    (
                                      <LeasingInfo onContinue={handleLeasingInfoNext} onTermsClick={handleTermsClick} />
                                      )
                                    : 
                                    currentStep === 1
                                      ? (
                                        <EMongolia
                                          instanceId={objs.instanceId ?? ''}
                                          errorType={errorType}
                                        />
                                        )
                                      : currentStep === 2
                                        ? (
                                          <ConfirmZMS onClick={handleInputNext} />
                                          )
                                        : currentStep === 3
                                          ? (
                                            <PaymentInfo onClick={handleInputNext} onClose={onClose} />
                                            )
                                          : null
                    )
                  : (
                    // <Disabled />
                    <>
                      {currentStep === 0
                        ? (
                            subStep === 'step1'
                              ? (
                                <Verification onClick={handleStep1Next} />
                                )
                              : subStep === 'number'
                                ? (
                                  <NumberInput
                                    onClick={handleInputNext}
                                    back={handleBack}
                                    instanceId={objs.instanceId ?? ''}
                                    initialPhoneNumber={objs.phoneNumber}
                                  />
                                  )
                                : (
                                  <EmailInput
                                    onClick={handleInputNext}
                                    back={handleBack}
                                    instanceId={objs.instanceId ?? ''}
                                    initialEmail={objs.email}
                                    initialPhoneNumber={objs.phoneNumber}
                                    choosenPackage={objs.choosenPackage}
                                  />
                                  )
                          )
                        : currentStep === 1
                          ? (
                            <EMongolia
                              instanceId={objs.instanceId ?? ''}
                            />
                            )
                          : currentStep === 2
                            ? (
                              <ConfirmZMS
                                onClick={handleInputNext}
                              />
                              )
                            : currentStep === 3
                              ? (
                                <PaymentInfo onClick={handleInputNext} onClose={onClose} />
                                )
                              : null}
                    </>
                    )}
              </div>
            </div>
          </>
          )}
    </div>
  )
}

export default LeasingServices
